/**
 * @fileoverview TileButton: the app's rounded-square tile, as Today's job card draws Edit, Postpone and Skip it and as the
 * plant card draws Share, Public, To-dos, Notes and Doctor (B136, design.md rev 12): one component on the shadcn Button with its
 * `tile` look and two sizes (styles/variants.ts), so every tile is the same soft muted square with 12px corners.
 *
 * Sizes: `big` is 48px tall with the icon beside the word (the plant card's first row); `small` is 44px tall with the icon over
 * the word. The states are props, and the component decides none of them (the caller's server answers do):
 * - `badge`: a count on the tile (terracotta on a small tile, a light chip on a filled one); nothing when empty;
 * - `filled`: Share's shine (style.css .share-shine) instead of the soft tint;
 * - `locked`: the lock in place of the icon (the plan or the owner keeps the thing from this gardener);
 * - `dim`: greyed and `aria-disabled`, but still a button so a tap can say why (a toast); never the disabled attribute;
 * - `open`: its tray is open, so it is filled with the accent and an edge;
 * - `due`: something on it is due today, so its words and icon are in the accent ink (terracotta);
 * - `to`: a router link in the tile's look (a paused plan's lock goes to Subscribe).
 * `aria-expanded` and every other button attribute pass through.
 *
 * Where: the plant card's tiles (CardTiles, PublicPageSwitch's tile look) and Today's job card (JobCard).
 * API: none.
 * Words: none; the caller gives the words.
 *
 * @module TileButton
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

/** What a tile is given: its size and states, an icon, a count, the words (children) and any button attribute. */
export interface TileButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** `big` (48px, icon beside the word) or `small` (44px, icon over the word). */
  size: 'big' | 'small';
  /** A count on the tile; nothing is drawn for empty, 0 or null. */
  badge?: number | string | null;
  /** The tile's icon, as an element (sized by the tile). */
  icon?: React.ReactNode;
  /** Draws Share's shine instead of the soft tint. */
  filled?: boolean;
  /** The lock in place of the icon. */
  locked?: boolean;
  /** Greyed and aria-disabled, still tappable (the caller says why in a toast). */
  dim?: boolean;
  /** Its tray is open: filled with the accent and an edge. */
  open?: boolean;
  /** Something is due today: the words and icon in the accent ink. */
  due?: boolean;
  /** Makes the tile a router link to this address, in the same look. */
  to?: string;
  /** The tile's words. */
  children: React.ReactNode;
}

/**
 * One tile: a button (or a link, with `to`) in the app's rounded-square look.
 *
 * @param props - TileButtonProps.
 */
export const TileButton = React.forwardRef<HTMLButtonElement, TileButtonProps>(
  ({ size, badge, icon, filled = false, locked = false, dim = false, open = false, due = false, to, className, children, ...rest }, ref) => {
    const big = size === 'big';
    const showBadge = badge !== undefined && badge !== null && badge !== 0 && badge !== '';
    const classes = cn(
      'min-w-0 whitespace-nowrap [&_svg]:shrink-0',
      big ? '[&_svg]:h-5 [&_svg]:w-5' : '[&_svg]:h-[1.125rem] [&_svg]:w-[1.125rem]',
      filled && 'share-shine text-share-foreground hover:bg-transparent active:bg-transparent',
      locked && !filled && 'text-muted-foreground',
      due && !open && 'text-accent-ink',
      open && 'bg-accent shadow-[inset_0_0_0_1.5px_hsl(var(--input))]',
      dim && 'opacity-50',
      className,
    );
    const inner = (
      <>
        {locked ? <Lock aria-hidden="true" /> : icon}
        {children}
        {showBadge && (
          <span
            className={cn(
              'min-w-[1.125rem] rounded-full px-1.5 text-[0.6875rem] font-bold leading-[1.125rem] tabular-nums',
              big ? 'bg-share-foreground/25 text-share-foreground' : 'absolute right-1.5 top-1 bg-highlight text-highlight-foreground',
            )}
          >
            {badge}
          </span>
        )}
      </>
    );
    const sizeName = big ? 'tileBig' : 'tileSmall';
    if (to) {
      return (
        <Button asChild variant="tile" size={sizeName} className={classes}>
          <Link to={to} title={rest.title} aria-label={rest['aria-label']}>{inner}</Link>
        </Button>
      );
    }
    return (
      <Button
        ref={ref}
        type="button"
        variant="tile"
        size={sizeName}
        className={classes}
        aria-disabled={dim ? true : undefined}
        {...rest}
      >
        {inner}
      </Button>
    );
  },
);
TileButton.displayName = 'TileButton';
